/**
 * BIRHAUS Adapter Utilities
 * 
 * Helpers to adapt existing toast libraries and components to BIRHAUS patterns.
 * Makes migration easier for teams already using other UI libraries.
 * 
 * Usage:
 * ```ts
 * import { createBirhausToastAdapter } from '@birhaus/primitives/adapters'
 * import { toast as reactHotToast } from 'react-hot-toast'
 * 
 * const birhausToast = createBirhausToastAdapter(reactHotToast)
 * birhausToast.showSuccess('Guardado exitosamente')
 * ```
 */

import type { BirhausToastData } from '../components/BirhausToast'

// Common toast library interfaces
interface GenericToast {
  (message: string, options?: any): any
  success?: (message: string, options?: any) => any
  error?: (message: string, options?: any) => any
  warning?: (message: string, options?: any) => any
  info?: (message: string, options?: any) => any
  dismiss?: (id?: any) => void
}

interface ReactHotToastLike {
  (message: string, options?: any): string
  success: (message: string, options?: any) => string
  error: (message: string, options?: any) => string
  loading: (message: string, options?: any) => string
  dismiss: (id?: string) => void
  remove: (id?: string) => void
}

interface ReactToastifyLike {
  success: (message: string, options?: any) => any
  error: (message: string, options?: any) => any
  warn: (message: string, options?: any) => any
  info: (message: string, options?: any) => any
  dismiss: (id?: any) => void
}

interface ChakraToastLike {
  (options: {
    title?: string
    description?: string
    status?: 'success' | 'error' | 'warning' | 'info'
    duration?: number
    isClosable?: boolean
  }): any
  closeAll: () => void
}

// BIRHAUS toast adapter interface
export interface BirhausToastAdapter {
  showSuccess: (message: string, options?: any) => any
  showError: (message: string, options?: any) => any
  showWarning: (message: string, options?: any) => any
  showInfo: (message: string, options?: any) => any
  showSaved: (entity?: string) => any
  showDeleted: (entity?: string) => any
  showPaymentSuccess: (amount?: string) => any
  dismiss: (id?: any) => void
  dismissAll: () => void
}

/**
 * Creates a BIRHAUS-compatible toast adapter from any toast library
 */
export function createBirhausToastAdapter(
  toast: GenericToast | ReactHotToastLike | ReactToastifyLike | ChakraToastLike,
  options: {
    locale?: 'es' | 'en'
    defaultDuration?: number
  } = {}
): BirhausToastAdapter {
  const { locale = 'es', defaultDuration = 5000 } = options

  // Helper to get Spanish-first messages
  const getMessage = (es: string, en: string) => locale === 'en' ? en : es

  // Detect toast library type and create appropriate adapter
  if ('success' in toast && 'error' in toast && 'dismiss' in toast) {
    // React Hot Toast style
    const hotToast = toast as ReactHotToastLike
    
    return {
      showSuccess: (message: string, opts?: any) => 
        hotToast.success(message, { duration: defaultDuration, ...opts }),
      
      showError: (message: string, opts?: any) => 
        hotToast.error(message, { duration: defaultDuration, ...opts }),
      
      showWarning: (message: string, opts?: any) => 
        hotToast(message, { 
          icon: '⚠️',
          duration: defaultDuration,
          style: { background: '#fef3c7', color: '#92400e' },
          ...opts 
        }),
      
      showInfo: (message: string, opts?: any) => 
        hotToast(message, { 
          icon: 'ℹ️',
          duration: defaultDuration,
          style: { background: '#dbeafe', color: '#1e40af' },
          ...opts 
        }),
      
      showSaved: (entity?: string) => {
        const message = entity 
          ? getMessage(`${entity} guardado exitosamente`, `${entity} saved successfully`)
          : getMessage('Guardado exitosamente', 'Saved successfully')
        return hotToast.success(message)
      },
      
      showDeleted: (entity?: string) => {
        const message = entity 
          ? getMessage(`${entity} eliminado exitosamente`, `${entity} deleted successfully`)
          : getMessage('Eliminado exitosamente', 'Deleted successfully')
        return hotToast.success(message)
      },
      
      showPaymentSuccess: (amount?: string) => {
        const message = amount 
          ? getMessage(`Pago de ${amount} procesado exitosamente`, `Payment of ${amount} processed successfully`)
          : getMessage('Pago procesado exitosamente', 'Payment processed successfully')
        return hotToast.success(message)
      },
      
      dismiss: (id?: string) => hotToast.dismiss(id),
      dismissAll: () => hotToast.dismiss()
    }
  }

  if ('warn' in toast && 'success' in toast) {
    // React Toastify style
    const toastify = toast as ReactToastifyLike
    
    return {
      showSuccess: (message: string, opts?: any) => 
        toastify.success(message, { autoClose: defaultDuration, ...opts }),
      
      showError: (message: string, opts?: any) => 
        toastify.error(message, { autoClose: defaultDuration, ...opts }),
      
      showWarning: (message: string, opts?: any) => 
        toastify.warn(message, { autoClose: defaultDuration, ...opts }),
      
      showInfo: (message: string, opts?: any) => 
        toastify.info(message, { autoClose: defaultDuration, ...opts }),
      
      showSaved: (entity?: string) => {
        const message = entity 
          ? getMessage(`${entity} guardado exitosamente`, `${entity} saved successfully`)
          : getMessage('Guardado exitosamente', 'Saved successfully')
        return toastify.success(message)
      },
      
      showDeleted: (entity?: string) => {
        const message = entity 
          ? getMessage(`${entity} eliminado exitosamente`, `${entity} deleted successfully`)
          : getMessage('Eliminado exitosamente', 'Deleted successfully')
        return toastify.success(message)
      },
      
      showPaymentSuccess: (amount?: string) => {
        const message = amount 
          ? getMessage(`Pago de ${amount} procesado exitosamente`, `Payment of ${amount} processed successfully`)
          : getMessage('Pago procesado exitosamente', 'Payment processed successfully')
        return toastify.success(message)
      },
      
      dismiss: (id?: any) => toastify.dismiss(id),
      dismissAll: () => toastify.dismiss()
    }
  }

  if ('closeAll' in toast) {
    // Chakra UI style
    const chakraToast = toast as ChakraToastLike
    
    return {
      showSuccess: (message: string, opts?: any) => 
        chakraToast({ 
          description: message, 
          status: 'success', 
          duration: defaultDuration,
          isClosable: true,
          ...opts 
        }),
      
      showError: (message: string, opts?: any) => 
        chakraToast({ 
          description: message, 
          status: 'error', 
          duration: defaultDuration,
          isClosable: true,
          ...opts 
        }),
      
      showWarning: (message: string, opts?: any) => 
        chakraToast({ 
          description: message, 
          status: 'warning', 
          duration: defaultDuration,
          isClosable: true,
          ...opts 
        }),
      
      showInfo: (message: string, opts?: any) => 
        chakraToast({ 
          description: message, 
          status: 'info', 
          duration: defaultDuration,
          isClosable: true,
          ...opts 
        }),
      
      showSaved: (entity?: string) => {
        const message = entity 
          ? getMessage(`${entity} guardado exitosamente`, `${entity} saved successfully`)
          : getMessage('Guardado exitosamente', 'Saved successfully')
        return chakraToast({ description: message, status: 'success' })
      },
      
      showDeleted: (entity?: string) => {
        const message = entity 
          ? getMessage(`${entity} eliminado exitosamente`, `${entity} deleted successfully`)
          : getMessage('Eliminado exitosamente', 'Deleted successfully')
        return chakraToast({ description: message, status: 'success' })
      },
      
      showPaymentSuccess: (amount?: string) => {
        const message = amount 
          ? getMessage(`Pago de ${amount} procesado exitosamente`, `Payment of ${amount} processed successfully`)
          : getMessage('Pago procesado exitosamente', 'Payment processed successfully')
        return chakraToast({ description: message, status: 'success' })
      },
      
      dismiss: () => {}, // Chakra doesn't support individual dismiss
      dismissAll: () => chakraToast.closeAll()
    }
  }

  // Generic fallback
  return {
    showSuccess: (message: string, opts?: any) => toast(message, opts),
    showError: (message: string, opts?: any) => toast(message, opts),
    showWarning: (message: string, opts?: any) => toast(message, opts),
    showInfo: (message: string, opts?: any) => toast(message, opts),
    
    showSaved: (entity?: string) => {
      const message = entity 
        ? getMessage(`${entity} guardado exitosamente`, `${entity} saved successfully`)
        : getMessage('Guardado exitosamente', 'Saved successfully')
      return toast(message)
    },
    
    showDeleted: (entity?: string) => {
      const message = entity 
        ? getMessage(`${entity} eliminado exitosamente`, `${entity} deleted successfully`)
        : getMessage('Eliminado exitosamente', 'Deleted successfully')
      return toast(message)
    },
    
    showPaymentSuccess: (amount?: string) => {
      const message = amount 
        ? getMessage(`Pago de ${amount} procesado exitosamente`, `Payment of ${amount} processed successfully`)
        : getMessage('Pago procesado exitosamente', 'Payment processed successfully')
      return toast(message)
    },
    
    dismiss: () => {},
    dismissAll: () => {}
  }
}

/**
 * Creates a migration helper for specific popular libraries
 */
export const createMigrationAdapter = {
  /**
   * Adapter for react-hot-toast
   */
  reactHotToast: (reactHotToast: ReactHotToastLike, locale: 'es' | 'en' = 'es') => 
    createBirhausToastAdapter(reactHotToast, { locale }),

  /**
   * Adapter for react-toastify
   */
  reactToastify: (reactToastify: ReactToastifyLike, locale: 'es' | 'en' = 'es') => 
    createBirhausToastAdapter(reactToastify, { locale }),

  /**
   * Adapter for Chakra UI toast
   */
  chakraUI: (chakraToast: ChakraToastLike, locale: 'es' | 'en' = 'es') => 
    createBirhausToastAdapter(chakraToast, { locale }),

  /**
   * Adapter for Ant Design notification
   */
  antDesign: (notification: any, locale: 'es' | 'en' = 'es') => ({
    showSuccess: (message: string) => notification.success({ message }),
    showError: (message: string) => notification.error({ message }),
    showWarning: (message: string) => notification.warning({ message }),
    showInfo: (message: string) => notification.info({ message }),
    showSaved: (entity?: string) => {
      const message = entity 
        ? (locale === 'es' ? `${entity} guardado exitosamente` : `${entity} saved successfully`)
        : (locale === 'es' ? 'Guardado exitosamente' : 'Saved successfully')
      return notification.success({ message })
    },
    showDeleted: (entity?: string) => {
      const message = entity 
        ? (locale === 'es' ? `${entity} eliminado exitosamente` : `${entity} deleted successfully`)
        : (locale === 'es' ? 'Eliminado exitosamente' : 'Deleted successfully')
      return notification.success({ message })
    },
    showPaymentSuccess: (amount?: string) => {
      const message = amount 
        ? (locale === 'es' ? `Pago de ${amount} procesado exitosamente` : `Payment of ${amount} processed successfully`)
        : (locale === 'es' ? 'Pago procesado exitosamente' : 'Payment processed successfully')
      return notification.success({ message })
    },
    dismiss: () => notification.destroy(),
    dismissAll: () => notification.destroy()
  })
}

/**
 * Creates adapter for custom toast implementations
 */
export function createCustomToastAdapter(config: {
  showToast: (message: string, type: 'success' | 'error' | 'warning' | 'info', options?: any) => any
  dismiss?: (id?: any) => void
  dismissAll?: () => void
  locale?: 'es' | 'en'
}): BirhausToastAdapter {
  const { showToast, dismiss = () => {}, dismissAll = () => {}, locale = 'es' } = config

  return {
    showSuccess: (message: string, opts?: any) => showToast(message, 'success', opts),
    showError: (message: string, opts?: any) => showToast(message, 'error', opts),
    showWarning: (message: string, opts?: any) => showToast(message, 'warning', opts),
    showInfo: (message: string, opts?: any) => showToast(message, 'info', opts),
    
    showSaved: (entity?: string) => {
      const message = entity 
        ? (locale === 'es' ? `${entity} guardado exitosamente` : `${entity} saved successfully`)
        : (locale === 'es' ? 'Guardado exitosamente' : 'Saved successfully')
      return showToast(message, 'success')
    },
    
    showDeleted: (entity?: string) => {
      const message = entity 
        ? (locale === 'es' ? `${entity} eliminado exitosamente` : `${entity} deleted successfully`)
        : (locale === 'es' ? 'Eliminado exitosamente' : 'Deleted successfully')
      return showToast(message, 'success')
    },
    
    showPaymentSuccess: (amount?: string) => {
      const message = amount 
        ? (locale === 'es' ? `Pago de ${amount} procesado exitosamente` : `Payment of ${amount} processed successfully`)
        : (locale === 'es' ? 'Pago procesado exitosamente' : 'Payment processed successfully')
      return showToast(message, 'success')
    },
    
    dismiss,
    dismissAll
  }
}

// Export types for adapter creation
export type { 
  GenericToast,
  ReactHotToastLike,
  ReactToastifyLike,
  ChakraToastLike
}